import { useId } from 'react';

import { Input } from '@/components/ui/input';
import { Label, RequiredMark } from '@/components/ui/label';

/** The one schedule shape every scheduled driver shares (Spec 082
 *  research.md D1) — `github`/`gitlab`, and now `workspace_task_change`
 *  schedule mode. */
export interface ScheduleFieldsValue {
  scheduleType: 'interval' | 'cron';
  intervalSeconds: number | null;
  cronExpression: string | null;
  timezone: string | null;
}

export interface ScheduleFieldsProps {
  value: ScheduleFieldsValue;
  onChange: (next: ScheduleFieldsValue) => void;
  /** Surfaces a server refusal attached to one schedule field, same lookup
   *  convention `TriggerActionFields`' `actionFieldError` established. */
  fieldError: (
    name: 'scheduleType' | 'intervalSeconds' | 'cronExpression' | 'timezone',
  ) => string | undefined;
  onClearError: () => void;
  disabled: boolean;
}

/**
 * The interval/cron radiogroup plus its cron-expression+timezone or
 * interval-seconds fields — extracted verbatim out of
 * `CreateTriggerProfileSheet`'s `github`/`gitlab` branch (Spec 082
 * research.md D1: two concrete call sites with an identical field set and
 * identical validation rules clears the "at least two adapters" bar, so this
 * extraction is the seam Constitution Rule 2.4 asks for, not a premature
 * one). Same JSX, same behavior, same validation-error surfacing convention
 * as before extraction — only the host component changed. Has no knowledge
 * of which driver hosts it (mirrors the "fields render, host behaves" split
 * `TriggerActionFields` already established).
 */
export function ScheduleFields({
  value,
  onChange,
  fieldError,
  onClearError,
  disabled,
}: ScheduleFieldsProps) {
  const scheduleGroupId = useId();
  const scheduleLabelId = useId();
  const intervalSecondsId = useId();
  const cronExpressionId = useId();
  const timezoneId = useId();

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <span id={scheduleLabelId} className="text-sm leading-none font-medium">
          Schedule
        </span>
        <div
          role="radiogroup"
          aria-labelledby={scheduleLabelId}
          className="flex gap-4"
        >
          {(['interval', 'cron'] as const).map((type) => (
            <label key={type} className="flex items-center gap-1.5 text-sm">
              <input
                type="radio"
                name={scheduleGroupId}
                value={type}
                checked={value.scheduleType === type}
                disabled={disabled}
                onChange={() => {
                  onChange({ ...value, scheduleType: type });
                  onClearError();
                }}
                className="accent-primary"
              />
              {type === 'interval' ? 'Interval' : 'Cron'}
            </label>
          ))}
        </div>
      </div>

      {value.scheduleType === 'cron' ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-0.5">
              <Label htmlFor={cronExpressionId}>Cron expression</Label>
              <RequiredMark />
            </div>
            <Input
              id={cronExpressionId}
              value={value.cronExpression ?? ''}
              disabled={disabled}
              placeholder="0 * * * *"
              className="font-mono"
              onChange={(event) => {
                const next = event.target.value;
                onChange({
                  ...value,
                  cronExpression: next.trim() === '' ? null : next,
                });
              }}
            />
            {fieldError('cronExpression') ? (
              <p className="text-destructive text-xs">
                {fieldError('cronExpression')}
              </p>
            ) : null}
          </div>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-0.5">
              <Label htmlFor={timezoneId}>Timezone</Label>
              <RequiredMark />
            </div>
            <Input
              id={timezoneId}
              value={value.timezone ?? ''}
              disabled={disabled}
              placeholder="UTC"
              onChange={(event) => {
                const next = event.target.value;
                onChange({
                  ...value,
                  timezone: next.trim() === '' ? null : next,
                });
              }}
            />
            {fieldError('timezone') ? (
              <p className="text-destructive text-xs">
                {fieldError('timezone')}
              </p>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-0.5">
            <Label htmlFor={intervalSecondsId}>Interval (seconds)</Label>
            <RequiredMark />
          </div>
          <Input
            id={intervalSecondsId}
            type="number"
            min={1}
            value={value.intervalSeconds ?? ''}
            disabled={disabled}
            placeholder="300"
            onChange={(event) => {
              const raw = event.target.value;
              onChange({
                ...value,
                intervalSeconds: raw === '' ? null : Number(raw),
              });
            }}
          />
          {fieldError('intervalSeconds') ? (
            <p className="text-destructive text-xs">
              {fieldError('intervalSeconds')}
            </p>
          ) : null}
        </div>
      )}
    </>
  );
}
